import { apiClient, endpoints } from '../api/client';

const STORE_PROVIDER_ID_KEY = 'storeProviderId';
const AZURE_JWT_SUB_KEY = 'azureJwtSub';

type AzureClaims = { oid?: string; sub?: string };

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Claims from the Azure id_token used for Store login. */
export async function getAzureIdClaims(): Promise<AzureClaims | null> {
  if (import.meta.env.VITE_USER_TOKEN) {
    const payload = parseJwtPayload(import.meta.env.VITE_USER_TOKEN as string);
    if (!payload) return null;
    return {
      oid: typeof payload.oid === 'string' ? payload.oid : undefined,
      sub: typeof payload.sub === 'string' ? payload.sub : undefined,
    };
  }

  try {
    const current = await apiClient.get(endpoints.currentUserToken);
    const parts = String(current.data).split(':');
    const token = parts[0];
    if (!token) return null;
    const payload = parseJwtPayload(token);
    if (!payload) return null;
    return {
      oid: typeof payload.oid === 'string' ? payload.oid : undefined,
      sub: typeof payload.sub === 'string' ? payload.sub : undefined,
    };
  } catch {
    return null;
  }
}

/** Canonical Store User.providerId (Azure `oid`), cached after Store login. */
export function getCachedStoreProviderId(): string | null {
  return window.localStorage.getItem(STORE_PROVIDER_ID_KEY);
}

export function getCachedAzureJwtSub(): string | null {
  return window.localStorage.getItem(AZURE_JWT_SUB_KEY);
}

export function setCachedStoreIdentity(providerId: string | null, jwtSub?: string | null): void {
  if (providerId) {
    window.localStorage.setItem(STORE_PROVIDER_ID_KEY, providerId);
  } else {
    window.localStorage.removeItem(STORE_PROVIDER_ID_KEY);
  }
  if (jwtSub) {
    window.localStorage.setItem(AZURE_JWT_SUB_KEY, jwtSub);
  }
}

/**
 * Provider ID to persist in updated_by — must match keys in GET /api/v1/store/users/names.
 * Store uses Azure JWT `oid`, not `sub`.
 */
export async function getCurrentProviderId(): Promise<string | null> {
  const cached = getCachedStoreProviderId();
  if (cached) {
    return cached;
  }

  const claims = await getAzureIdClaims();
  if (claims?.oid) {
    return claims.oid;
  }
  if (claims?.sub) {
    return claims.sub;
  }
  return null;
}
